import React, { useState, useCallback } from "react"
import BuilderVariableChip from "./BuilderVariableChip.component"
import {
  QC_BUILDER_DRAG_TYPE,
  QC_BUILDER_DRAG_LEAVES_TYPE,
} from "stores/queryBuilderStore"

interface BuilderDropZoneProps {
  label: string
  variables: string[]
  onAdd: (variable: string) => void
  onRemove: (variable: string) => void
  emptyHint?: string
  /**
   * When true, dropping a parent QTable expands it to its leaf descendants
   * (each becomes its own chip via repeated onAdd calls). Used by the
   * Conditions builder, where APPENDing a parent collapses to "any value"
   * and produces a useless filter. Rows/Columns leave this off — XTAB/DTAB
   * accept parent QTables natively.
   */
  expandParents?: boolean
}

/**
 * Reusable drop zone for Rows / Columns. Accepts variable paths via native
 * HTML5 drag-and-drop using the QC_BUILDER_DRAG_TYPE MIME type set by the
 * DataTree, or plain text fallback.
 */
const BuilderDropZone: React.FC<BuilderDropZoneProps> = ({
  label,
  variables,
  onAdd,
  onRemove,
  emptyHint = "Drag variables here",
  expandParents = false,
}) => {
  const [isDragOver, setIsDragOver] = useState(false)

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (
      e.dataTransfer.types.includes(QC_BUILDER_DRAG_TYPE) ||
      e.dataTransfer.types.includes("text/plain")
    ) {
      e.preventDefault()
      e.dataTransfer.dropEffect = "copy"
    }
  }, [])

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (
      e.dataTransfer.types.includes(QC_BUILDER_DRAG_TYPE) ||
      e.dataTransfer.types.includes("text/plain")
    ) {
      setIsDragOver(true)
    }
  }, [])

  const handleDragLeave = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      // Only clear when leaving the zone itself, not bubbling from children
      if (e.currentTarget === e.target) {
        setIsDragOver(false)
      }
    },
    []
  )

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      setIsDragOver(false)

      if (expandParents) {
        const leavesPayload = e.dataTransfer.getData(
          QC_BUILDER_DRAG_LEAVES_TYPE
        )
        if (leavesPayload) {
          try {
            const leaves: unknown = JSON.parse(leavesPayload)
            if (Array.isArray(leaves) && leaves.length > 0) {
              leaves.forEach((leaf) => {
                if (typeof leaf === "string" && leaf.trim()) {
                  onAdd(leaf.trim())
                }
              })
              return
            }
          } catch {
            // Malformed payload — fall through to the single-path branch.
          }
        }
      }

      const variable =
        e.dataTransfer.getData(QC_BUILDER_DRAG_TYPE) ||
        e.dataTransfer.getData("text/plain")
      if (variable) {
        onAdd(variable.trim())
      }
    },
    [onAdd, expandParents]
  )

  return (
    <div className="qc-dropzone-wrapper">
      <div className="qc-dropzone-label">{label}</div>
      <div
        className="qc-dropzone"
        data-state={
          isDragOver ? "active" : variables.length === 0 ? "empty" : "filled"
        }
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        role="list"
        aria-label={`${label} variables`}
      >
        {variables.length === 0 ? (
          <span className="qc-dropzone__empty">{emptyHint}</span>
        ) : (
          variables.map((variable) => (
            <BuilderVariableChip
              key={variable}
              variable={variable}
              onRemove={onRemove}
            />
          ))
        )}
      </div>
    </div>
  )
}

export default BuilderDropZone
